import { makeStyles, TextField } from "@material-ui/core";
import { ClassNameMap } from "@material-ui/core/styles/withStyles";
import { useEffect, useState } from "react";

export interface InputFieldProps<T> {
  label?: string;
  initialValue?: T;
  calculatedValue?: T|null;
  disabled?: boolean;
  autoFocus?: boolean;
  classes?: ClassNameMap<'inputField'>;
  onValueChange?: (value: T) => void;
  errorMessage?: (value: string|null) => string|null;
}

interface TypedInputFieldProps<T> extends InputFieldProps<T> {
  castToType: (valueAsText: string) => T;
  convertValueToText: (value: T) => string;
}

const useStyles = makeStyles(theme => ({
  inputField: {
    marginBottom: theme.spacing(1)
  }
}));

const InputField = <T,>(props: TypedInputFieldProps<T>) => {
  const initialValue = props.initialValue;
  const calculatedValue = props.calculatedValue;
  const defaultClasses = useStyles()
  const classes = props.classes || defaultClasses;
  const convertValueToText = (value: T|undefined) => value === undefined || value === null ? null : props.convertValueToText(value);
  const [valueAsText, setValueAsText] = useState<string|null>(convertValueToText(initialValue));
  const errorMessage = props.errorMessage ? props.errorMessage(valueAsText) : null;

  const changeValue = (value: T) => {
    props.onValueChange && props.onValueChange(value);
  }

  const handleValueChange = (valueAsText: string) => {
    setValueAsText(valueAsText);
    const value = props.castToType(valueAsText);
    changeValue(value);
  };

  useEffect(() => {
    setValueAsText(convertValueToText(initialValue));
    console.debug(`## InputField / initial value changed: ${props.label} : ${valueAsText} -> ${initialValue}`);
    if (initialValue !== undefined && initialValue !== null) {
      console.debug(`## InputField / skipping emitting initial value as a change: ${props.label} : ${valueAsText} -> ${initialValue}`);
      changeValue(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    if (calculatedValue !== undefined) {
      setValueAsText(calculatedValue === null ? '' : convertValueToText(calculatedValue));
      console.debug(`## InputField / calculated value changed: ${props.label} : ${valueAsText} -> ${calculatedValue}`);
    }
  }, [calculatedValue]);

  useEffect(() => {
    console.debug(`## InputField / initial rendering: ${props.label} : ${initialValue}`);
  }, []);

  return (
    <TextField
      autoFocus={props.autoFocus}
      className={classes.inputField} 
      disabled={props.disabled || false}
      label={props.label}
      onChange={event => handleValueChange(event.target.value)}
      value={valueAsText || ''}
      error={errorMessage !== null}
      helperText={errorMessage}
    />
  );
}
export default InputField;